/**
 * Is the location permission *refused by the person*, or merely unavailable?
 *
 * The distinction decides whether W8's « Position non transmise » screen is
 * shown at all, and getting it wrong in the permissive direction would put an
 * extra tap in front of every punch on the plain-HTTP dev build — where
 * `navigator.geolocation` exists and nothing it does can ever succeed
 * (local dev is plain HTTP by design; HTTPS is production-only).
 *
 * So only an explicit `denied` gates. `prompt`, `granted`, a browser with no
 * Permissions API, an insecure context and any thrown error all answer
 * `unknown`, and `captureGps()` handles them the way D-09 requires: eight
 * seconds, a `gpsStatus`, and the punch goes anyway.
 */
export type GpsPermission = 'denied' | 'granted' | 'prompt' | 'unknown';

interface PermissionsLike {
  readonly query: (descriptor: { readonly name: PermissionName }) => Promise<{ state: string }>;
}

function permissionsOf(): PermissionsLike | null {
  if (typeof navigator === 'undefined') return null;
  if (typeof isSecureContext === 'boolean' && !isSecureContext) return null;
  const candidate = (navigator as { permissions?: PermissionsLike }).permissions;
  return candidate ?? null;
}

export async function probeGpsPermission(): Promise<GpsPermission> {
  const permissions = permissionsOf();
  if (permissions === null) return 'unknown';
  try {
    const status = await permissions.query({ name: 'geolocation' as PermissionName });
    if (status.state === 'denied') return 'denied';
    if (status.state === 'granted') return 'granted';
    if (status.state === 'prompt') return 'prompt';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}
